import { useMemo } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from '@/contexts/AuthProvider';
import { WorkspaceProvider } from '@/contexts/WorkspaceProvider';
import { useWorkspaceState } from '@/contexts/WorkspaceContext';
import { queryClient } from '@/lib/queryClient';
import { resolveRoute, type RouteTarget } from '@/lib/workspace';
import { MarketingRoutes } from '@/routes/marketing';
import { AdminRoutes } from '@/routes/admin';
import { WorkspaceRoutes } from '@/routes/workspace';
import { InlineBrandStyle } from '@/components/workspace/InlineBrandStyle';
import { LoadingSplash } from '@/components/workspace/LoadingSplash';
import { WorkspaceNotFoundPage } from '@/components/workspace/WorkspaceNotFoundPage';
import { WorkspaceSuspendedPage } from '@/components/workspace/WorkspaceSuspendedPage';
import { WorkspaceExpiredPage } from '@/components/workspace/WorkspaceExpiredPage';
import { WorkspaceUnavailablePage } from '@/components/workspace/WorkspaceUnavailablePage';
import { WorkspaceDeniedPage } from '@/components/workspace/WorkspaceDeniedPage';
import { WorkspaceErrorPage } from '@/components/workspace/WorkspaceErrorPage';
import { ReservedSubdomainPage } from '@/components/workspace/ReservedSubdomainPage';
import { Toaster } from '@/components/ui/toaster';
import './App.css';

function WorkspaceShell() {
  const state = useWorkspaceState();
  switch (state.kind) {
    case 'apex':
    case 'loading':
      return <LoadingSplash />;
    case 'not-found':
      return <WorkspaceNotFoundPage slug={state.slug} />;
    case 'suspended':
      return <WorkspaceSuspendedPage reason={state.reason} />;
    case 'expired':
      return <WorkspaceExpiredPage account={state.account} />;
    case 'unavailable':
      return <WorkspaceUnavailablePage account={state.account} reason={state.reason} />;
    case 'denied':
      return <WorkspaceDeniedPage account={state.account} />;
    case 'error':
      return <WorkspaceErrorPage />;
    case 'active':
      return (
        <>
          <InlineBrandStyle colors={state.account.settings?.branding?.brand_colors} />
          <WorkspaceRoutes />
        </>
      );
  }
}

function AppShell({ target }: { target: RouteTarget }) {
  if (target.kind === 'reserved') {
    return <ReservedSubdomainPage sub={target.sub} />;
  }

  if (target.kind === 'marketing') {
    // Marketing site is auth-less and doesn't read the workspace context, but we
    // still mount the provider in `apex` mode so shared components that import
    // `useAccount` (e.g. PublicUploadPage) don't blow up.
    return (
      <WorkspaceProvider slug={null}>
        <MarketingRoutes />
      </WorkspaceProvider>
    );
  }

  if (target.kind === 'admin') {
    return (
      <WorkspaceProvider slug={null}>
        <AdminRoutes />
      </WorkspaceProvider>
    );
  }

  return (
    <WorkspaceProvider slug={target.slug}>
      <WorkspaceShell />
    </WorkspaceProvider>
  );
}

function App() {
  const target = useMemo(() => resolveRoute(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppShell target={target} />
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      <Analytics />
      <SpeedInsights />
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
